import React, { Component, createRef } from 'react';
import { createPopper, Instance } from '@popperjs/core';
import styles from './header-v2.component.module.scss';
import ContactUsModal from '../ui/contact-us-modal/contact-us.modal';
import { lazyInject } from '../../context/module';
import { IOC_TYPES } from '../../context/ioc.types';
import { LoginService } from '../../services/login.service';
import { UIContext } from '../../services/ui-context';
import { ScriptLoadService } from '../../services/script-load.service';
import { FileManagerService } from '../../services/file-manager.service';
import { FileUpload } from '../../models/file-upload';
import { BulkZipService } from '../../services/bulk-zip.service';
import { Subscription } from 'rxjs';
import { ToolsDropdownColumn } from 'xconvert-web-shared';

import { HeaderSearch } from './header-search.component';
import { ToolsPanel } from './tools-panel.component';

interface NavItem {
    name: string;
    href?: string;
    submenu?: NavItem[];
    onClick?: (e: React.MouseEvent) => void;
    /** If true, render the Tools mega-menu instead of a generic submenu */
    isToolsMenu?: boolean;
}

const resourcesNav: NavItem = {
    name: 'Resources', href: '#', submenu: [
        { name: 'Blog', href: '/blog' },
        { name: 'Contact', href: '/contact-us' }
    ]
};

interface NavbarState {
    isMobileMenuOpen: boolean;
    openSubmenu: string | null;
    isLoggedIn: boolean;
    isInitialized: boolean;
    isUserMenuOpen: boolean;
    downloadCount: number;
    bulkZipActiveCount: number;
}

interface NavbarProps {
    toolsDropdownData?: ToolsDropdownColumn[];
}

class Navbar extends Component<NavbarProps, NavbarState> {
    // Refs for the buttons and submenus that Popper will use
    private navRef = createRef<HTMLDivElement>();
    private menuButtonRefs: { [key: string]: React.RefObject<HTMLButtonElement> } = {};
    private submenuRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    // Refs for User Menu
    private userMenuButtonRef = createRef<HTMLDivElement>();
    private userMenuRef = createRef<HTMLDivElement>();

    private popperInstances: { [key: string]: Instance } = {};
    private userDetailsSubscription: Subscription;

    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(BulkZipService) private bulkZipService: BulkZipService;

    private bulkZipSubscription: Subscription;

    constructor(props: NavbarProps) {
        super(props);
        this.state = {
            isMobileMenuOpen: false,
            openSubmenu: null,
            isLoggedIn: this.loginService.isUserLoggedIn, // synchronous check if available
            isInitialized: false,
            isUserMenuOpen: false,
            downloadCount: 0,
            bulkZipActiveCount: 0,
        };

        // Initialize refs for each item that has a submenu
        this.getNavItems().forEach(item => {
            if (item.submenu || item.isToolsMenu) {
                this.menuButtonRefs[item.name] = createRef<HTMLButtonElement>();
                this.submenuRefs[item.name] = createRef<HTMLDivElement>();
            }
        });
    }

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);

        // Initial check and subscription
        this.userDetailsSubscription = this.loginService.onUserDetailsChanged.subscribe((userDetails) => {
            this.setState({
                isLoggedIn: !!userDetails,
                isInitialized: true
            });
        });

        this.fileManagerService.fileUploadsListChanged.subscribe({
            next: (fileUploads: FileUpload[]) => this.setState({ downloadCount: fileUploads.length }),
        });

        this.bulkZipSubscription = this.bulkZipService.jobs$.subscribe((jobs) =>
            this.setState({ bulkZipActiveCount: jobs.filter((j) => j.active).length }),
        );

        // If we already have details synchronously available (some services might), init immediately
        if (this.loginService.userDetails) {
            this.setState({ isInitialized: true, isLoggedIn: true });
        } else {
            // Force initialization after a short timeout if no userDetails emission happens (guest state)
            // But usually LoginService should emit null if not logged in.
            // Just in case, we can set isInitialized true if we are sure it emits.
            // Assuming LoginService emits initial state.
        }
    }

    componentWillUnmount() {
        Object.values(this.popperInstances).forEach(instance => instance.destroy());
        document.removeEventListener("mousedown", this.handleClickOutside);
        if (this.userDetailsSubscription) {
            this.userDetailsSubscription.unsubscribe();
        }
        if (this.bulkZipSubscription) {
            this.bulkZipSubscription.unsubscribe();
        }
    }

    handleClickOutside = (event: MouseEvent) => {
        const { openSubmenu } = this.state;
        if (!openSubmenu) return;

        const buttonEl = this.menuButtonRefs[openSubmenu]?.current;
        const submenuEl = this.submenuRefs[openSubmenu]?.current;

        if (
            buttonEl &&
            submenuEl &&
            !buttonEl.contains(event.target as Node) &&
            !submenuEl.contains(event.target as Node)
        ) {
            this.setState({ openSubmenu: null });
        }

        // Handle clicking outside User Menu
        const userBtn = this.userMenuButtonRef.current;
        const userMenu = this.userMenuRef.current;
        if (
            this.state.isUserMenuOpen &&
            userBtn &&
            userMenu &&
            !userBtn.contains(event.target as Node) &&
            !userMenu.contains(event.target as Node)
        ) {
            this.toggleUserMenu(); // Close it
        }
    };

    handleSubmenuItemClick = () => {
        this.setState({ openSubmenu: null, isMobileMenuOpen: false });
    };

    toggleMobileMenu = () => {
        this.setState(prevState => ({
            isMobileMenuOpen: !prevState.isMobileMenuOpen,
            openSubmenu: null, // Close submenu when closing mobile nav
        }));
    };

    toggleUserMenu = () => {
        const isOpening = !this.state.isUserMenuOpen;

        // Clean up existing user menu popper if it exists
        if (this.popperInstances['userMenu']) {
            this.popperInstances['userMenu'].destroy();
            delete this.popperInstances['userMenu'];
        }

        this.setState({ isUserMenuOpen: isOpening }, () => {
            if (isOpening && this.userMenuButtonRef.current && this.userMenuRef.current) {
                this.popperInstances['userMenu'] = createPopper(
                    this.userMenuButtonRef.current,
                    this.userMenuRef.current,
                    {
                        placement: 'bottom-end',
                        modifiers: [
                            {
                                name: 'offset',
                                options: { offset: [0, 8] },
                            },
                        ],
                    }
                );
            }
        });
    }

    // This function now creates and destroys Popper instances
    toggleSubmenu = (submenuName: string) => {
        const isAlreadyOpen = this.state.openSubmenu === submenuName;

        // First, destroy any existing popper instance if a menu is closing
        if (this.state.openSubmenu && this.popperInstances[this.state.openSubmenu]) {
            this.popperInstances[this.state.openSubmenu].destroy();
            delete this.popperInstances[this.state.openSubmenu];
        }

        this.setState({
            openSubmenu: isAlreadyOpen ? null : submenuName,
        }, () => {
            // If a new menu is being opened, create a Popper for it
            const newOpenSubmenu = this.state.openSubmenu;
            if (newOpenSubmenu) {
                const button = this.menuButtonRefs[newOpenSubmenu]?.current;
                const submenu = this.submenuRefs[newOpenSubmenu]?.current;

                // On mobile the submenu is rendered inline inside .navItem and
                // styled with position:relative — let CSS handle placement so
                // the panel appears directly under the Tools row. An earlier
                // version passed navRef.current as the Popper parent here,
                // which dragged the submenu to the bottom of the entire nav
                // menu (after Resources + Downloads) instead of under Tools.
                const isMobileView = window.innerWidth <= 768;
                if (isMobileView) return;

                if (button && submenu) {
                    this.popperInstances[newOpenSubmenu] = createPopper(button, submenu, {
                        placement: 'bottom',
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 8], // 8px distance from the button
                                },
                            },
                        ],
                    });
                }
            }
        });
    }

    private async showContactUs(e: React.MouseEvent) {
        e.preventDefault();
        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        ContactUsModal.show({});
    }

    private handleLogout = (e: React.MouseEvent) => {
        e.preventDefault();
        this.loginService.logout().subscribe(() => {
            // Optional: Redirect to home if needed, or just stay on page as state updates
            window.location.href = '/';
        });
    }

    getNavItems = (): NavItem[] => {
        return [
            { name: 'Tools', href: '#', isToolsMenu: true },
            resourcesNav,
        ];
    }

    renderToolsMegaMenu() {
        // S6.2 — replaced the 109-link mega-menu with the compact ToolsPanel.
        // The previous version was a 14-column wall of links with no search,
        // unusable on phones (hidden entirely below 768px), and shipped two
        // duplicated PDF columns. ToolsPanel keeps the same prop contract
        // (toolsDropdownData stays available for backward compat) but renders
        // search + popular chips + category tiles instead. See
        // components/header/tools-panel.component.tsx.
        return (
            <ToolsPanel onClose={this.handleSubmenuItemClick} />
        );
    }

    renderRightActions(isMobile: boolean = false) {

        if (this.state.isLoggedIn && isMobile) {
            const userEmail = this.loginService.userDetails?.email || 'User';
            const userInitial = userEmail.charAt(0).toUpperCase();
            return (
                <div className={styles.mobileUserSection}>
                    <div className={styles.mobileUserProfile}>
                        <div className={styles.userAvatarMobile}>{userInitial}</div>
                        <div className={styles.mobileUserEmail}>{userEmail}</div>
                    </div>
                    <a href="/portal" className={styles.mobileActionLink} onClick={() => this.setState({ isMobileMenuOpen: false })}>Dashboard</a>
                    <a href="#" onClick={(e) => { this.handleLogout(e); this.setState({ isMobileMenuOpen: false }); }} className={styles.mobileActionLink}>Log Out</a>
                </div>
            );
        }

        const isLoggedIn = this.state.isLoggedIn;
        const userDetails = this.loginService.userDetails;
        const isPaidUser = isLoggedIn && userDetails && typeof userDetails.isPaidUser === 'function' ? userDetails.isPaidUser() : false;

        // Show button if user is NOT paid (Guest or Free)
        const showPremiumButton = !isPaidUser;
        const premiumButtonText = isLoggedIn ? "Upgrade" : "Pricing";

        const userEmail = isLoggedIn ? (userDetails?.email || 'User') : '';
        const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : '';

        return (
            <div className={styles.rightActions}>

                {/* Bulk-ZIP pill - shown when the user has any active bulk-zip jobs (building/ready/failed). */}
                {!isMobile && this.state.bulkZipActiveCount > 0 && (
                    <button
                        type="button"
                        className={styles.actionLink}
                        onClick={() => this.bulkZipService.openCenter()}
                        aria-label={`Bulk downloads (${this.state.bulkZipActiveCount})`}
                    >
                        📦 {this.state.bulkZipActiveCount}
                    </button>
                )}

                {/* Premium Action Button - Visible for Guests and Free Users */}
                {!isMobile && showPremiumButton && (
                    <a href="/product" className={`${styles.actionLink} ${styles.primaryAction}`}>{premiumButtonText}</a>
                )}

                {/* Stable User Icon Container */}
                <div className={styles.userAvatarContainer}>
                    {isLoggedIn ? (
                        /* Logged In: Clickable Avatar */
                        <div
                            className={styles.userAvatarBtn}
                            ref={this.userMenuButtonRef}
                            onClick={this.toggleUserMenu}
                        >
                            {userInitial}
                        </div>
                    ) : (
                        /* Guest: Link to Login with Outline Icon */
                        <a href="/portal" className={`${styles.userAvatarBtn} ${styles.guestIcon}`}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </a>
                    )}

                    {/* Dropdown Menu (Only renders if logged in and open) */}
                    {isLoggedIn && this.state.isUserMenuOpen && (
                        <div className={styles.userDropdown} ref={this.userMenuRef}>
                            <div className={styles.userDropdownHeader}>
                                <div className={styles.userDropdownEmail}>{userEmail}</div>
                            </div>
                            <div className={styles.userDropdownDivider}></div>
                            <a href="/portal" className={styles.userDropdownItem}>Dashboard</a>
                            <a href="#" onClick={this.handleLogout} className={styles.userDropdownItem}>Log Out</a>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { isMobileMenuOpen, openSubmenu } = this.state;
        const currentNavItems = this.getNavItems();

        return (
            <nav className={`${styles.navbar} ${styles['mobile-fixed-nav']} shadow-sm bg-white`} data-xc-page-top="">
                <div className={styles.navbarContainer}>
                    <a href="/" className={styles.logo}>XConvert</a>
                    <div className={styles.mobileSearch}>
                        <HeaderSearch />
                    </div>
                    <div className={styles.menuIcon} onClick={this.toggleMobileMenu}>
                        <div className={`${styles.bar} ${isMobileMenuOpen ? styles.animateBar1 : ''}`}></div>
                        <div className={`${styles.bar} ${isMobileMenuOpen ? styles.animateBar2 : ''}`}></div>
                        <div className={`${styles.bar} ${isMobileMenuOpen ? styles.animateBar3 : ''}`}></div>
                    </div>
                    <div className={`${styles.navMenu} ${isMobileMenuOpen ? styles.active : ''}`} ref={this.navRef}>
                        {currentNavItems.map(item => (
                            <div key={item.name} className={styles.navItem}>
                                {item.isToolsMenu ? (
                                    <>
                                        <button
                                            ref={this.menuButtonRefs[item.name]}
                                            className={styles.navLink}
                                            onClick={() => this.toggleSubmenu(item.name)}
                                        >
                                            {item.name}
                                            <span className={`${styles.arrow} ${openSubmenu === item.name ? styles.arrowUp : ''}`}>&#9662;</span>
                                        </button>
                                        {openSubmenu === item.name && (
                                            <div
                                                ref={this.submenuRefs[item.name]}
                                                className={styles.submenu}
                                            >
                                                {this.renderToolsMegaMenu()}
                                            </div>
                                        )}
                                    </>
                                ) : item.submenu ? (
                                    <>
                                        <button
                                            ref={this.menuButtonRefs[item.name]}
                                            className={styles.navLink}
                                            onClick={() => this.toggleSubmenu(item.name)}
                                        >
                                            {item.name}
                                            <span className={`${styles.arrow} ${openSubmenu === item.name ? styles.arrowUp : ''}`}>&#9662;</span>
                                        </button>
                                        {openSubmenu === item.name && (
                                            <div
                                                ref={this.submenuRefs[item.name]}
                                                className={styles.submenu}
                                            >
                                                <div className='col-sm-12'>
                                                    {item.submenu.map(subItem => (
                                                        <a
                                                            key={subItem.name}
                                                            href={subItem.href}
                                                            className={styles.submenuItem}
                                                            onClick={this.handleSubmenuItemClick}
                                                        >
                                                            {subItem.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    item.onClick ?
                                        <a href={item.href || '#'} className={styles.navLink} onClick={item.onClick}>{item.name}</a>
                                        :
                                        <a href={item.href} className={styles.navLink}>{item.name}</a>
                                )}
                            </div>
                        ))}
                        {/* Downloads Link */}
                        <div className={styles.navItem}>
                            <a href="/downloads" className={styles.navLink}>
                                Downloads
                                {this.state.downloadCount > 0 && <span className={styles.badge}>{this.state.downloadCount}</span>}
                            </a>
                        </div>
                        <div className={styles.mobileOnlyActions}>
                            {this.renderRightActions(true)}
                        </div>
                    </div>

                    {/* Right Actions Desktop */}
                    <div className={styles.desktopActions}>
                        <HeaderSearch />
                        {this.renderRightActions(false)}
                    </div>
                </div>
            </nav >
        );
    }
}

export default Navbar;
